import React from 'react';
import LegalPage, { Section, List, Link } from '@/components/Pages/LegalPage';
import Paragraph from '@/components/Typography/Paragraph';
import MetaHead from '@/components/MetaHead';

const DMCAPage = () => {
  return (
    <LegalPage
      title="Copyright Policy"
      lastUpdated="8 January 2024"
    >
      <MetaHead 
        title="Copyright Policy"
        description="Learn about our copyright policy, including how to submit DMCA takedown notices and counter-notifications. Understand your rights and responsibilities regarding copyright-protected content on Gab AI's platform."
      />

      <Section title="Reporting Claims of Copyright Infringement">
        <Paragraph>
          We take claims of copyright infringement seriously. We will respond to notices of alleged copyright infringement that comply with applicable law. If you believe any materials accessible on or from this site (the &quot;Website&quot;) infringe your copyright, you may request removal of those materials (or access to them) from the Website by submitting written notification to our copyright agent designated below. In accordance with the Online Copyright Infringement Liability Limitation Act of the Digital Millennium Copyright Act (17 U.S.C. § 512) (&quot;DMCA&quot;), the written notice (the &quot;DMCA Notice&quot;) must include substantially the following:
        </Paragraph>
        
        <List items={[
          'Your physical or electronic signature.',
          'Identification of the copyrighted work you believe to have been infringed or, if the claim involves multiple works on the Website, a representative list of such works.',
          'Identification of the material you believe to be infringing in a sufficiently precise manner to allow us to locate that material.',
          'Adequate information by which we can contact you (including your name, postal address, telephone number, and, if available, email address).',
          'A statement that you have a good faith belief that use of the copyrighted material is not authorized by the copyright owner, its agent, or the law.',
          'A statement that the information in the written notice is accurate.',
          'A statement, under penalty of perjury, that you are authorized to act on behalf of the copyright owner.'
        ]} />

        <Paragraph>
          Please send copyright notices to:
        </Paragraph>
        <Paragraph>
          Gab AI Inc.<br />
          1143 Northern Blvd #158<br />
          Clarks Summit, PA 18411
        </Paragraph>
        <Paragraph>
          Or via any contact form at the bottom of this page.
        </Paragraph>
        <Paragraph>
          If you fail to comply with all of the requirements of Section 512(c)(3) of the DMCA, your DMCA Notice may not be effective.
        </Paragraph>
        <Paragraph>
          Please be aware that if you knowingly materially misrepresent that material or activity on the Website is infringing your copyright, you may be held liable for damages (including costs and attorneys&apos; fees) under Section 512(f) of the DMCA.
        </Paragraph>
      </Section>

      <Section title="Counter Notification Procedures">
        <Paragraph>
          If you believe that material you posted on the Website was removed or access to it was disabled by mistake or misidentification, you may file a counter notification with us (a &quot;Counter Notice&quot;) by submitting written notification to our DMCA address (above) Pursuant to the DMCA, the Counter Notice must include substantially the following:
        </Paragraph>

        <List items={[
          'Your physical or electronic signature.',
          'An identification of the material that has been removed or to which access has been disabled and the location at which the material appeared before it was removed or access disabled.',
          'Adequate information by which we can contact you (including your name, postal address, telephone number, and, if available, email address).',
          'A statement under penalty of perjury by you that you have a good faith belief that the material identified above was removed or disabled as a result of a mistake or misidentification of the material to be removed or disabled.',
          'A statement that you will consent to the jurisdiction of the Federal District Court for the judicial district in which your address is located (or if you reside outside the United States for any judicial district in which the Website may be found) and that you will accept service from the person (or an agent of that person) who provided the Website with the complaint at issue.'
        ]} />

        <Paragraph>
          The DMCA allows us to restore the removed content if the party filing the original DMCA Notice does not file a court action against you within ten business days of receiving the copy of your Counter Notice.
        </Paragraph>
        <Paragraph>
          Please be aware that if you knowingly materially misrepresent that material or activity on the Website was removed or disabled by mistake or misidentification, you may be held liable for damages (including costs and attorneys&apos; fees) under Section 512(f) of the DMCA.
        </Paragraph>
      </Section>

      <Section title="Repeat Infringers">
        <Paragraph>
          It is our policy in appropriate circumstances to disable and/or terminate the accounts of users who are repeat infringers.
        </Paragraph>
        <Paragraph>
          <Link href="mailto:ai@gab.com">ai@gab.com</Link>
        </Paragraph>
      </Section>
    </LegalPage>
  );
};

export default React.memo(DMCAPage);
